import React, { useState } from "react";
import {
  Card,
  Col,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  Alert,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import {
  DownloadOutlined,
  TagOutlined,
  MoneyCollectOutlined,
} from "@ant-design/icons";
import RobinPage from "../components/RobinPage";
import {
  useGetAllPartnersSelectQuery,
  useGetPartnerPromoTrackingQuery,
} from "../graphql/generated/hooks";
import { usePermission } from "../hooks/usePermission";
import { PromoCodeDiscountType } from "../graphql/generated/schemas";

const { Text } = Typography;

interface PromoCodeRow {
  key: number;
  code: string;
  discountType: PromoCodeDiscountType;
  discountValue: number;
  usage: string;
  validFrom?: string | null;
  validTo?: string | null;
  isActive: boolean;
  storeId?: number | null;
}

interface DownloadRow {
  key: string;
  label: string;
  count: number;
}

function PartnerTracking() {
  const { t } = useTranslation();
  const [selectedPartnerId, setSelectedPartnerId] = useState<number | null>(
    null
  );

  const perms = usePermission(["getAllPromoCodes", "getPartnerDownloads"]);

  const { data: partnersData, loading: partnersLoading } =
    useGetAllPartnersSelectQuery();

  const { data, loading } = useGetPartnerPromoTrackingQuery({
    variables: { partnerId: selectedPartnerId! },
    skip: selectedPartnerId == null,
    fetchPolicy: "network-only",
  });

  const tracking = data?.getPartnerPromoTracking;

  const hasPermission =
    perms["getAllPromoCodes"] && perms["getPartnerDownloads"];

  const formatDate = (date?: string | null) => {
    if (!date) return "—";
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return date;
    return d.toLocaleDateString();
  };

  const promoCodeColumns: ColumnsType<PromoCodeRow> = [
    {
      title: t("label.promo_code.code"),
      dataIndex: "code",
      key: "code",
      render: (code: string) => <Text strong>{code}</Text>,
    },
    {
      title: t("label.promo_code.discount_type"),
      dataIndex: "discountType",
      key: "discountType",
      render: (discountType: PromoCodeDiscountType) =>
        discountType === PromoCodeDiscountType.Percentage
          ? t("label.promo_code.percentage")
          : t("label.promo_code.fixed"),
    },
    {
      title: t("label.promo_code.discount_value"),
      dataIndex: "discountValue",
      key: "discountValue",
      render: (value: number, record: PromoCodeRow) =>
        `${value}${
          record.discountType === PromoCodeDiscountType.Percentage
            ? "%"
            : ` ${t("label.currency")}`
        }`,
    },
    {
      title: t("label.promo_code.usage_count"),
      dataIndex: "usage",
      key: "usage",
    },
    {
      title: t("label.promo_code.validity"),
      key: "validity",
      render: (_, record: PromoCodeRow) => (
        <Space direction="vertical" size={0}>
          <Text type="secondary">
            {t("label.promo_code.starts_at")}: {formatDate(record.validFrom)}
          </Text>
          <Text type="secondary">
            {t("label.promo_code.ends_at")}: {formatDate(record.validTo)}
          </Text>
        </Space>
      ),
    },
    {
      title: t("label.promo_code.store"),
      dataIndex: "storeId",
      key: "storeId",
      render: (storeId: number | null | undefined) =>
        storeId ?? t("label.promo_code.global"),
    },
    {
      title: t("label.promo_code.is_active"),
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive: boolean) =>
        isActive ? (
          <Tag color="green">{t("button.activate")}</Tag>
        ) : (
          <Tag color="red">{t("button.deactivate")}</Tag>
        ),
    },
  ];

  const promoCodeDataSource: PromoCodeRow[] = (tracking?.promoCodes ?? []).map(
    (promoCode) => ({
      key: promoCode.id,
      code: promoCode.code,
      discountType: promoCode.discountType,
      discountValue: promoCode.discountValue,
      usage: `${promoCode.usageCount}${
        promoCode.usageLimit != null ? ` / ${promoCode.usageLimit}` : ""
      }`,
      validFrom: promoCode.startsAt,
      validTo: promoCode.endsAt,
      isActive: promoCode.isActive,
      storeId: promoCode.storeId,
    })
  );

  const downloadByCodeColumns: ColumnsType<DownloadRow> = [
    {
      title: t("label.tracking.code"),
      dataIndex: "label",
      key: "label",
      render: (label: string) => <Text code>{label}</Text>,
    },
    {
      title: t("label.tracking.downloads"),
      dataIndex: "count",
      key: "count",
      align: "right",
    },
  ];

  const downloadByPlatformColumns: ColumnsType<DownloadRow> = [
    {
      title: t("label.tracking.platform"),
      dataIndex: "label",
      key: "label",
    },
    {
      title: t("label.tracking.downloads"),
      dataIndex: "count",
      key: "count",
      align: "right",
    },
  ];

  const byCodeSource: DownloadRow[] = (tracking?.downloads.byCode ?? []).map(
    (item) => ({
      key: item.code ?? t("label.tracking.no_code"),
      label: item.code ?? t("label.tracking.no_code"),
      count: item.count,
    })
  );

  const byPlatformSource: DownloadRow[] = (
    tracking?.downloads.byPlatform ?? []
  ).map((item) => ({
    key: item.platform ?? "web",
    label: item.platform ?? "web",
    count: item.count,
  }));

  const activeCodesCount = (tracking?.promoCodes ?? []).filter(
    (promoCode) => promoCode.isActive
  ).length;
  const totalUsages = (tracking?.promoCodes ?? []).reduce(
    (sum, promoCode) => sum + promoCode.usageCount,
    0
  );

  if (!hasPermission) {
    return (
      <RobinPage title={t("label.menu.partner_tracking")}>
        <Alert
          message={t("error.access_denied")}
          description={t("error.insufficient_permissions")}
          type="error"
          showIcon
          style={{ margin: "20px 0" }}
        />
      </RobinPage>
    );
  }

  return (
    <RobinPage title={t("label.menu.partner_tracking")}>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Card
          title={
            <Space>
              <TagOutlined />
              <span>{t("label.tracking.select_partner")}</span>
            </Space>
          }
        >
          <Select
            placeholder={t("label.partner.select")}
            allowClear
            showSearch
            style={{ width: "100%", maxWidth: 480 }}
            loading={partnersLoading}
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            options={partnersData?.getAllPartners?.map((partner) => ({
              label: `${partner.name} (${partner.nameAr})`,
              value: partner.id,
            }))}
            onChange={(value) => setSelectedPartnerId(value)}
          />
        </Card>

        {tracking && (
          <>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8}>
                <Card
                  style={{
                    borderRadius: "8px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                  }}
                >
                  <Statistic
                    title={
                      <span style={{ color: "rgba(255,255,255,0.85)" }}>
                        {t("label.tracking.total_downloads")}
                      </span>
                    }
                    value={tracking.downloads.total}
                    prefix={<DownloadOutlined style={{ color: "#fff" }} />}
                    valueStyle={{ color: "#fff", fontSize: 32, fontWeight: "bold" }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Card
                  style={{
                    borderRadius: "8px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    background: "linear-gradient(135deg, #52c41a 0%, #97f2a1 100%)",
                  }}
                >
                  <Statistic
                    title={
                      <span style={{ color: "rgba(255,255,255,0.9)" }}>
                        {t("label.tracking.active_codes")}
                      </span>
                    }
                    value={activeCodesCount}
                    prefix={<TagOutlined style={{ color: "#fff" }} />}
                    valueStyle={{ color: "#fff", fontSize: 32, fontWeight: "bold" }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Card
                  style={{
                    borderRadius: "8px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                  }}
                >
                  <Statistic
                    title={
                      <span style={{ color: "rgba(255,255,255,0.85)" }}>
                        {t("label.tracking.total_usages")}
                      </span>
                    }
                    value={totalUsages}
                    prefix={<MoneyCollectOutlined style={{ color: "#fff" }} />}
                    valueStyle={{ color: "#fff", fontSize: 32, fontWeight: "bold" }}
                  />
                </Card>
              </Col>
            </Row>

            <Card
              title={
                <Space>
                  <TagOutlined />
                  <span>{t("label.menu.promo_codes")}</span>
                </Space>
              }
            >
              <Table
                columns={promoCodeColumns}
                dataSource={promoCodeDataSource}
                loading={loading}
                pagination={{ pageSize: 10 }}
              />
            </Card>

            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <Card
                  title={
                    <Space>
                      <DownloadOutlined />
                      <span>{t("label.tracking.by_code")}</span>
                    </Space>
                  }
                >
                  <Table
                    columns={downloadByCodeColumns}
                    dataSource={byCodeSource}
                    loading={loading}
                    pagination={false}
                    size="small"
                  />
                </Card>
              </Col>
              <Col xs={24} md={12}>
                <Card
                  title={
                    <Space>
                      <DownloadOutlined />
                      <span>{t("label.tracking.by_platform")}</span>
                    </Space>
                  }
                >
                  <Table
                    columns={downloadByPlatformColumns}
                    dataSource={byPlatformSource}
                    loading={loading}
                    pagination={false}
                    size="small"
                  />
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Space>
    </RobinPage>
  );
}

export default PartnerTracking;